import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CG_DISTRICT_LIST, CG_DISTRICTS } from "@/lib/cgLocations";
import { Newspaper, Send, LogOut, Camera, MapPin, LogIn, CheckCircle2, Clock, X, Upload, MessageSquare } from "lucide-react";
import ReporterChatTab from "@/components/ReporterChatTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "in21_reporter_token";

// Standalone axios instance for reporter (Bearer token, not cookies).
function useReporterApi() {
  const client = axios.create({ baseURL: API });
  client.interceptors.request.use((c) => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) c.headers.Authorization = `Bearer ${t}`;
    return c;
  });
  return client;
}

export default function ReporterAppPage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const client = useReporterApi();

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    client.get("/reporter/me").then((r) => setMe(r.data)).catch(() => localStorage.removeItem(TOKEN_KEY)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">लोड हो रहा है…</div>;
  if (!me) return <LoginScreen onLoggedIn={setMe}/>;
  return <ReporterDashboard me={me} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setMe(null); }}/>;
}

function LoginScreen({ onLoggedIn }) {
  const [staffId, setStaffId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/reporter/login`, { staff_id: staffId.trim().toUpperCase(), pin: pin.trim() });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLoggedIn(data.reporter);
      toast.success(`स्वागत है, ${data.reporter.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "लॉगिन नहीं हो सका");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] flex items-center justify-center p-4" data-testid="reporter-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <img src="/logo.png" alt="" className="w-12 h-12 rounded-lg bg-white p-1"/>
            <div className="text-left">
              <div className="text-white font-devnagri-heading font-black text-xl leading-none">INDIAN-NEWS-21</div>
              <div className="text-amber-300 text-[10px] uppercase tracking-widest font-bold">Reporter Beat App</div>
            </div>
          </div>
          <div className="uppercase tracking-[0.3em] text-[11px] text-amber-300 font-bold">फ़ील्ड रिपोर्टर लॉगिन</div>
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-widest">स्टाफ ID</label>
              <input type="text" placeholder="SMR-001" value={staffId} onChange={(e) => setStaffId(e.target.value)} required
                className="w-full mt-1 border border-slate-300 rounded-md px-4 py-3 text-lg font-mono uppercase focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="reporter-staff-id"/>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-widest">4-अंकीय PIN</label>
              <input type="password" inputMode="numeric" maxLength={6} placeholder="••••" value={pin} onChange={(e) => setPin(e.target.value)} required
                className="w-full mt-1 border border-slate-300 rounded-md px-4 py-3 text-lg tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="reporter-pin"/>
            </div>
            <button type="submit" disabled={busy} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-[#0F172A] font-black py-3 rounded-md flex items-center justify-center gap-2 shadow-lg disabled:opacity-60" data-testid="reporter-login-btn">
              <LogIn className="w-4 h-4"/>{busy ? "लॉगिन कर रहे…" : "लॉगिन करें"}
            </button>
          </form>
          <div className="mt-5 pt-4 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed">
            <b>PIN नहीं है?</b> अपने संपादक/एडमिन से संपर्क करें — वे आपके स्टाफ profile में 4-अंकीय PIN सेट करेंगे।
          </div>
          <Link to="/" className="mt-4 block text-center text-[11px] text-slate-400 hover:text-slate-700">← मुखपृष्ठ</Link>
        </div>
      </div>
    </div>
  );
}

const CATS = ["स्थानीय", "राजनीति", "अपराध", "खेल", "व्यापार", "मनोरंजन", "तकनीक", "अंतरराष्ट्रीय", "अन्य"];

function ReporterDashboard({ me, onLogout }) {
  const client = useReporterApi();
  const [tab, setTab] = useState("submit");
  const [form, setForm] = useState({ title: "", category: "स्थानीय", excerpt: "", content: "", image_url: "", district: "", block: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const districts = CG_DISTRICT_LIST;
  const blocks = form.district ? (CG_DISTRICTS[form.district] || []) : [];

  const loadSubmissions = () => client.get("/reporter/my-submissions").then((r) => setSubmissions(r.data)).catch(() => {});
  useEffect(() => { loadSubmissions(); /* eslint-disable-next-line */ }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await client.post("/reporter/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("फ़ोटो अपलोड हो गयी");
    } catch (err) { toast.error(err?.response?.data?.detail || "फ़ोटो अपलोड नहीं हुई"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) { toast.error("शीर्षक और विवरण ज़रूरी है"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(), category: form.category, excerpt: form.excerpt.trim(),
        content: form.content.trim(), image_url: form.image_url || null,
        district: form.district || null, block: form.block || null,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      await client.post("/reporter/submit", payload);
      toast.success("समाचार जमा हो गया — एडमिन समीक्षा के बाद प्रकाशित होगा");
      setForm({ title: "", category: "स्थानीय", excerpt: "", content: "", image_url: "", district: "", block: "", tags: "" });
      loadSubmissions();
      setTab("submissions");
    } catch (err) { toast.error(err?.response?.data?.detail || "जमा नहीं हो सका"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reporter-dashboard">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 bg-[#0F172A] text-white shadow-md">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/logo.png" alt="" className="w-9 h-9 rounded bg-white p-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-devnagri-heading font-black text-sm leading-none">{me.name}</div>
            <div className="text-[10px] text-amber-300 uppercase tracking-widest">{me.staff_id} · {me.designation}</div>
          </div>
          <button onClick={onLogout} className="p-2 rounded-full hover:bg-white/10" title="लॉगआउट" data-testid="reporter-logout">
            <LogOut className="w-4 h-4"/>
          </button>
        </div>
        {/* Tabs */}
        <div className="max-w-2xl mx-auto px-4 flex">
          <button onClick={() => setTab("submit")} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest border-b-2 flex items-center justify-center gap-1.5 ${tab === "submit" ? "border-amber-400 text-amber-300" : "border-transparent text-slate-400"}`} data-testid="tab-submit">
            <Send className="w-3.5 h-3.5"/>नया समाचार
          </button>
          <button onClick={() => setTab("submissions")} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest border-b-2 flex items-center justify-center gap-1.5 ${tab === "submissions" ? "border-amber-400 text-amber-300" : "border-transparent text-slate-400"}`} data-testid="tab-submissions">
            <Newspaper className="w-3.5 h-3.5"/>मेरे समाचार ({submissions.length})
          </button>
          <button onClick={() => setTab("chat")} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest border-b-2 flex items-center justify-center gap-1.5 ${tab === "chat" ? "border-amber-400 text-amber-300" : "border-transparent text-slate-400"}`} data-testid="tab-chat">
            <MessageSquare className="w-3.5 h-3.5"/>चैट
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {tab === "submit" ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <label className="text-[11px] font-bold text-slate-600 uppercase tracking-widest block mb-1">शीर्षक</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="जैसे: सूरजपुर में सड़क हादसा"
                className="w-full border border-slate-200 rounded-md px-3 py-3 text-base font-devnagri-heading font-bold focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="submit-title" required/>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-600 uppercase tracking-widest block mb-1">वर्ग</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm bg-white" data-testid="submit-category">
                  {CATS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-600 uppercase tracking-widest block mb-1">टैग्स</label>
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="अल्पविराम से अलग"
                  className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm"/>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-3.5 h-3.5 text-emerald-600"/>
                <label className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">स्थान (छत्तीसगढ़)</label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value, block: "" })}
                  className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm bg-white" data-testid="submit-district">
                  <option value="">जिला चुनें</option>
                  {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select value={form.block} onChange={(e) => setForm({ ...form, block: e.target.value })} disabled={!form.district}
                  className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm bg-white disabled:bg-slate-100" data-testid="submit-block">
                  <option value="">ब्लॉक चुनें</option>
                  {blocks.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <label className="text-[11px] font-bold text-slate-600 uppercase tracking-widest block mb-1">सारांश (Excerpt)</label>
              <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2}
                placeholder="1-2 पंक्ति में मुख्य बात"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"/>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <label className="text-[11px] font-bold text-slate-600 uppercase tracking-widest block mb-1">पूरी ख़बर</label>
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8}
                placeholder="विस्तार से लिखिये — पैराग्राफ अलग करने के लिए दो बार Enter दबाएँ"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none leading-relaxed font-serif-hi"
                data-testid="submit-content" required/>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-3.5 h-3.5 text-rose-600"/>
                <label className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">फ़ोटो अपलोड</label>
              </div>
              {form.image_url ? (
                <div className="relative">
                  <img src={form.image_url} alt="" className="w-full aspect-video object-cover rounded-md"/>
                  <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5" data-testid="submit-photo-remove">
                    <X className="w-4 h-4"/>
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md py-8 cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition">
                  <Upload className="w-6 h-6 text-slate-400"/>
                  <span className="text-xs text-slate-600 font-medium">{uploading ? "अपलोड हो रहा…" : "फ़ोटो चुनें / कैमरा खोलें"}</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="submit-photo-input"/>
                </label>
              )}
            </div>

            <button type="submit" disabled={busy} className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-black py-3.5 rounded-md flex items-center justify-center gap-2 shadow-lg disabled:opacity-60 text-base" data-testid="submit-btn">
              <Send className="w-4 h-4"/>{busy ? "भेज रहे…" : "समाचार भेजें"}
            </button>
            <div className="text-[11px] text-slate-500 text-center leading-relaxed">
              आपका समाचार एडमिन (JAKIR MOMIN) की समीक्षा के बाद प्रकाशित होगा।
            </div>
          </form>
        ) : (
          <div className="space-y-3" data-testid="submissions-list">
            {submissions.length === 0 && (
              <div className="text-center py-12 text-slate-500 bg-white rounded-xl border border-dashed border-slate-200">
                अभी तक कोई समाचार जमा नहीं किया
              </div>
            )}
            {submissions.map((s) => (
              <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`sub-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.is_published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {s.is_published ? <CheckCircle2 className="w-5 h-5"/> : <Clock className="w-5 h-5"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-devnagri-heading font-bold text-[#0F172A] text-sm leading-snug">{s.title}</div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span className="text-[#EA580C] font-bold uppercase tracking-wider text-[10px]">{s.category}</span>
                      {s.district && <span>• {s.district}{s.block ? `, ${s.block}` : ""}</span>}
                      <span>• {new Date(s.created_at).toLocaleString("hi-IN")}</span>
                    </div>
                    <div className={`inline-block mt-2 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${s.is_published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {s.is_published ? "प्रकाशित" : "समीक्षाधीन"}
                    </div>
                    {s.is_published && s.slug && (
                      <Link to={`/article/${s.slug}`} className="ml-2 mt-2 inline-block text-[11px] text-blue-600 hover:underline">देखें →</Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
